import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  User, 
  ShieldCheck, 
  LogOut, 
  PlusCircle, 
  RefreshCw, 
  CheckCircle2, 
  AlertTriangle,
  Users,
  Edit2,
  Trash2,
  Ban,
  CheckCircle,
  X
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function DeveloperPortal({ lang = 'mr' }) {
  const isMr = lang === 'mr';

  // Auth State
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('milkpro_dev_token'));
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const saved = localStorage.getItem('milkpro_dev_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Login Form State
  const [loginForm, setLoginForm] = useState({
    username: 'developer',
    password: ''
  });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // Portal Data State
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [provisioningTenantId, setProvisioningTenantId] = useState(null);

  // Tenant Database Reset Modal State
  const [resetModalTenant, setResetModalTenant] = useState(null);
  const [resetTables, setResetTables] = useState([]);
  const [selectedResetTables, setSelectedResetTables] = useState([]);
  const [loadingResetTables, setLoadingResetTables] = useState(false);
  const [resetConfirmChecked, setResetConfirmChecked] = useState(false);
  const [submittingReset, setSubmittingReset] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const openResetDatabaseModal = async (tenant) => {
    setResetModalTenant(tenant);
    setSelectedResetTables([]);
    setResetConfirmChecked(false);
    setResetResult(null);
    setLoadingResetTables(true);
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${tenant.id}/table-counts`);
      const data = await res.json();
      if (data.success && Array.isArray(data.tables)) {
        setResetTables(data.tables);
      }
    } catch (e) {
      console.error('Failed to fetch table counts', e);
    } finally {
      setLoadingResetTables(false);
    }
  };

  const handleToggleResetTable = (tblName) => {
    setSelectedResetTables(prev => 
      prev.includes(tblName) ? prev.filter(t => t !== tblName) : [...prev, tblName]
    );
  };

  const handleSelectAllResetTables = () => {
    if (selectedResetTables.length === resetTables.length) {
      setSelectedResetTables([]);
    } else {
      setSelectedResetTables(resetTables.map(t => t.table));
    }
  };

  const handleExecuteResetDatabase = async () => {
    if (!resetModalTenant || selectedResetTables.length === 0) return;
    const confirmMsg = isMr
      ? `काय तुम्ही नक्की निवडलेले ${selectedResetTables.length} टेबल्स पूर्णपणे ब्लॅंक (Truncate) करू इच्छिता? हा डेटा कायमस्वरूपी नष्ट होईल!`
      : `Are you sure you want to permanently blank/truncate ${selectedResetTables.length} tables? This action cannot be undone!`;
    if (!window.confirm(confirmMsg)) return;

    setSubmittingReset(true);
    setResetResult(null);
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${resetModalTenant.id}/reset-database`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tables: selectedResetTables })
      });
      const data = await res.json();
      if (data.success) {
        setResetResult(data);
        const cntRes = await fetch(`${API_BASE}/developer/public-tenants/${resetModalTenant.id}/table-counts`);
        const cntData = await cntRes.json();
        if (cntData.success && Array.isArray(cntData.tables)) {
          setResetTables(cntData.tables);
        }
        setSelectedResetTables([]);
        setResetConfirmChecked(false);
      } else {
        alert(data.message || 'Error resetting database');
      }
    } catch (err) {
      alert('Network error while resetting database: ' + err.message);
    } finally {
      setSubmittingReset(false);
    }
  };

  const handleCopyTenantLink = (slug) => {
    const url = `${window.location.origin}/#${slug}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2500);
  };

  const handleShareWhatsApp = (t) => {
    const url = `${window.location.origin}/#${t.slug}`;
    const ownerGreeting = t.owner_name ? `नमस्कार ${t.owner_name}जी, ` : 'नमस्कार, ';
    const dairyName = t.brand_name_mr || t.name;
    const msg = `${ownerGreeting}\nतुमचे *${dairyName}* चे *MilkPro Mitra* डेअरी मॅनेजमेंट सॉफ्टवेअर आणि ॲप तयार आहे!\n\nखालील लिंकवर क्लिक करून थेट लॉगिन करा:\n🔗 ${url}\n\nसॉफ्टवेअर मोबाईलवर इन्स्टॉल करण्यासाठी लिंक ओपन केल्यावर 'Install App' बटण दाबा.\n\n- e.Sense InfoSolutions`;
    
    const cleanPhone = (t.phone || '').replace(/[^0-9]/g, '');
    const finalPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
    const waUrl = `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank');
  };

  // New Tenant Form State
  const [formData, setFormData] = useState({
    name: '',
    brand_name_mr: '',
    brand_name_en: '',
    owner_name: '',
    phone: '',
    email: '',
    city: '',
    address: '',
    upi_id: '',
    plan_id: 2,
    valid_until: '2027-09-30',
    payment_status: 'PAID',
    payment_remarks: 'वार्षिक आगाऊ भरणा (Yearly Advance)'
  });

  // =========================================================================
  // TENANT USER MANAGEMENT MODAL STATE
  // =========================================================================
  const [userModalTenant, setUserModalTenant] = useState(null); // Selected tenant for user mgmt
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantCenters, setTenantCenters] = useState([]);
  const [tenantLines, setTenantLines] = useState([]);
  const [maxUsersAllowed, setMaxUsersAllowed] = useState(2);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userActionMsg, setUserActionMsg] = useState(null);
  const [userActionError, setUserActionError] = useState(null);

  // Add / Edit User Form State
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [userForm, setUserForm] = useState({
    id: null,
    name: '',
    username: '',
    phone: '',
    password: '',
    role: 'DELIVERY',
    center_access: 'ALL',
    assigned_line_id: ''
  });
  const [savingUser, setSavingUser] = useState(false);

  // Edit Tenant Modal & Form State
  const [editTenantModal, setEditTenantModal] = useState(false);
  const [editTenantData, setEditTenantData] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      const res = await fetch(`${API_BASE}/developer/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      const data = await res.json();
      if (data.success && data.token) {
        localStorage.setItem('milkpro_dev_token', data.token);
        localStorage.setItem('milkpro_dev_user', JSON.stringify(data.user));
        setAuthToken(data.token);
        setCurrentUser(data.user);
        setLoginForm({ username: 'developer', password: '' });
      } else {
        setLoginError(data.message || (isMr ? 'अवैध युझरनेम किंवा पासवर्ड' : 'Invalid username or password'));
      }
    } catch (err) {
      setLoginError((isMr ? 'बॅकएंड सर्व्हरशी संपर्क होऊ शकला नाही: ' : 'Cannot connect to backend: ') + err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem('milkpro_dev_token');
    localStorage.removeItem('milkpro_dev_user');
    setAuthToken(null);
    setCurrentUser(null);
    setTenants([]);
    setSuccessMsg(null);
    setError(null);
  };

  // Load Portal Data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      const [tRes, pRes] = await Promise.all([
        fetch(`${API_BASE}/developer/public-tenants`, { headers }),
        fetch(`${API_BASE}/developer/plans`, { headers })
      ]);
      const tData = await tRes.json();
      const pData = await pRes.json();
      if (tData.success) setTenants(tData.tenants || []);
      if (pData.success) setPlans(pData.plans || []);
    } catch (err) {
      console.error('API Error:', err);
      setError(isMr ? 'माहिती लोड करता आली नाही. कृपया API चालू असल्याची खात्री करा.' : 'Failed to load data from API.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Create Tenant Handler
  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const payload = {
        ...formData,
        brand_name_mr: formData.brand_name_mr || formData.name,
        brand_name_en: formData.brand_name_en || formData.name
      };

      const res = await fetch(`${API_BASE}/developer/public-tenants`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setSuccessMsg(isMr 
          ? `✅ अभिनंदन! नवीन Tenant "${data.tenant.name}" यशस्वीरित्या ऑनबोर्ड झाला आणि डेटाबेस "${data.provision.database_name}" ऑटो-मायग्रेट झाला!`
          : `Tenant ${data.tenant.name} provisioned in DB ${data.provision.database_name}`);
        setShowModal(false);
        setFormData({
          name: '',
          brand_name_mr: '',
          brand_name_en: '',
          owner_name: '',
          phone: '',
          email: '',
          city: '',
          address: '',
          upi_id: '',
          plan_id: plans[0]?.id || 2,
          valid_until: '2027-09-30',
          payment_status: 'PAID',
          payment_remarks: ''
        });
        loadData();
      } else {
        setError(data.error || data.message || (isMr ? 'Tenant तयार करताना त्रुटी आली.' : 'Failed to create tenant.'));
      }
    } catch (err) {
      setError((isMr ? 'सर्व्हरशी संपर्क होऊ शकला नाही: ' : 'Server error: ') + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  
  const handleReprovisionTenant = async (t) => {
    const confirmMsg = isMr 
      ? `डेटाबेस "${t.database_name}" मायग्रेट व सिंक करायचा आहे का? सर्व आवश्यक टेबल्स व डिफॉल्ट डेटाबेस रेकॉर्ड्स आपोआप तयार होतील.`
      : `Migrate & Provision database "${t.database_name}"?`;
    
    if (!window.confirm(confirmMsg)) return;

    setProvisioningTenantId(t.id);
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${t.id}/provision`, {
        method: 'POST',
        headers: authToken ? { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        alert(isMr 
          ? `🎉 अभिनंदन! डेटाबेस "${t.database_name}" यशस्वीरित्या मायग्रेट झाला आणि सर्व टेबल्स तयार झाले!`
          : `Database ${t.database_name} migrated successfully!`
        );
        loadData();
      } else {
        alert(data.provision?.error || data.error || data.message || (isMr ? 'डेटाबेस मायग्रेट करताना त्रुटी आली. कृपया Hostinger hPanel मध्ये डेटाबेस व युझर परवानग्या तपासा.' : 'Failed to migrate DB.'));
      }
    } catch (err) {
      console.error(err);
      alert(isMr ? 'सर्व्हरशी संपर्क होऊ शकला नाही: ' + err.message : 'Server connection failed: ' + err.message);
    } finally {
      setProvisioningTenantId(null);
    }
  };

  const handleToggleStatus = async (tenantId) => {
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${tenantId}/status`, {
        method: 'PATCH',
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      const data = await res.json();
      if (data.success) {
        setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: data.tenant.status } : t));
      }
    } catch (err) {
      alert('Error toggling status: ' + err.message);
    }
  };

  const openEditTenant = (t) => {
    const feat = t.feature || {};
    setEditTenantData({
      id: t.id,
      name: t.name || '',
      brand_name_mr: t.brand_name_mr || '',
      brand_name_en: t.brand_name_en || '',
      owner_name: t.owner_name || '',
      phone: t.phone || '',
      email: t.email || '',
      city: t.city || '',
      address: t.address || '',
      plan_id: t.plan_id || 1,
      valid_until: t.valid_until ? t.valid_until.split('T')[0] : '',
      payment_status: t.payment_status || 'PAID',
      payment_remarks: t.payment_remarks || '',
      max_users_allowed: feat.max_users_allowed || 2,
      max_centers_allowed: feat.max_centers_allowed || 1,
      counter_pos: feat.counter_pos !== undefined ? Boolean(feat.counter_pos) : true,
      daily_runsheet: feat.daily_runsheet !== undefined ? Boolean(feat.daily_runsheet) : true,
      online_orders: feat.online_orders !== undefined ? Boolean(feat.online_orders) : true,
      dairy_byproducts: feat.dairy_byproducts !== undefined ? Boolean(feat.dairy_byproducts) : true,
      bakery_products: feat.bakery_products !== undefined ? Boolean(feat.bakery_products) : true,
      whatsapp_billing: feat.whatsapp_billing !== undefined ? Boolean(feat.whatsapp_billing) : ((t.plan_id || 1) >= 2),
    });
    setEditTenantModal(t);
  };

  const handleSaveTenantDetails = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${editTenantData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          name: editTenantData.name,
          brand_name_mr: editTenantData.brand_name_mr,
          brand_name_en: editTenantData.brand_name_en,
          owner_name: editTenantData.owner_name,
          phone: editTenantData.phone,
          email: editTenantData.email,
          city: editTenantData.city,
          address: editTenantData.address,
          plan_id: parseInt(editTenantData.plan_id, 10),
          valid_until: editTenantData.valid_until || null,
          payment_status: editTenantData.payment_status,
          payment_remarks: editTenantData.payment_remarks,
          features: {
            counter_pos: editTenantData.counter_pos,
            daily_runsheet: editTenantData.daily_runsheet,
            online_orders: editTenantData.online_orders,
            dairy_byproducts: editTenantData.dairy_byproducts,
            bakery_products: editTenantData.bakery_products,
            whatsapp_billing: Boolean(editTenantData.whatsapp_billing),
            max_users_allowed: parseInt(editTenantData.max_users_allowed, 10) || 2,
            max_centers_allowed: parseInt(editTenantData.max_centers_allowed, 10) || 1
          }
        })
      });
      const data = await res.json();
      if (data.success) {
        setTenants(prev => prev.map(t => t.id === editTenantData.id ? data.tenant : t));
        setEditTenantModal(null);
      } else {
        alert(data.message || 'Error updating tenant details');
      }
    } catch (err) {
      alert('Error updating tenant: ' + err.message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleToggleFeature = async (tenantId, featureKey, currentValue) => {
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${tenantId}/features`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          [featureKey]: !currentValue
        })
      });
      const data = await res.json();
      if (data.success) {
        setTenants(prev => prev.map(t => {
          if (t.id === tenantId) {
            return {
              ...t,
              feature: data.feature
            };
          }
          return t;
        }));
      }
    } catch (err) {
      alert('Error toggling feature: ' + err.message);
    }
  };

  // =========================================================================
  // USER MANAGEMENT HANDLERS (Add / Modify / Delete Users in Tenant DB)
  // =========================================================================

  const openUserManagement = async (tenant) => {
    setUserModalTenant(tenant);
    setLoadingUsers(true);
    setUserActionMsg(null);
    setUserActionError(null);
    setIsEditingUser(false);
    setUserForm({
      id: null,
      name: '',
      username: '',
      phone: '',
      password: '',
      role: 'DELIVERY',
      center_access: 'ALL',
      assigned_line_id: ''
    });

    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${tenant.id}/users`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      const data = await res.json();
      if (data.success) {
        setTenantUsers(data.users || []);
        setTenantCenters(data.centers || []);
        setTenantLines(data.lines || []);
        setMaxUsersAllowed(data.max_users_allowed || 2);
      } else {
        setUserActionError(data.message || 'युझर्स माहिती लोड करता आली नाही.');
      }
    } catch (err) {
      setUserActionError('सर्व्हर एरर: ' + err.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    setUserActionMsg(null);
    setUserActionError(null);

    const tenantId = userModalTenant.id;
    const isEdit = isEditingUser && userForm.id;
    const url = isEdit 
      ? `${API_BASE}/developer/public-tenants/${tenantId}/users/${userForm.id}`
      : `${API_BASE}/developer/public-tenants/${tenantId}/users`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const payload = {
        name: userForm.name,
        username: userForm.username,
        phone: userForm.phone,
        role: userForm.role,
        center_access: userForm.center_access || 'ALL',
        assigned_line_id: userForm.assigned_line_id ? parseInt(userForm.assigned_line_id) : null
      };
      if (userForm.password) {
        payload.password = userForm.password;
      }

      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setUserActionMsg(data.message || 'युझर यशस्वीरित्या सेव्ह झाला!');
        setIsEditingUser(false);
        setUserForm({
          id: null,
          name: '',
          username: '',
          phone: '',
          password: '',
          role: 'DELIVERY',
          center_access: 'ALL',
          assigned_line_id: ''
        });
        // Reload users
        openUserManagement(userModalTenant);
      } else {
        setUserActionError(data.message || 'युझर सेव्ह करताना त्रुटी आली.');
      }
    } catch (err) {
      setUserActionError('सर्व्हर एरर: ' + err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const handleEditClick = (u) => {
    setIsEditingUser(true);
    setUserForm({
      id: u.id,
      name: u.name,
      username: u.username,
      phone: u.phone || '',
      password: '', // blank unless changing
      role: u.role,
      center_access: u.center_access || 'ALL',
      assigned_line_id: u.assigned_line_id ? u.assigned_line_id.toString() : ''
    });
    setUserActionMsg(null);
    setUserActionError(null);
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${userModalTenant.id}/users/${userId}/status`, {
        method: 'PATCH',
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      const data = await res.json();
      if (data.success) {
        setTenantUsers(prev => prev.map(u => u.id === userId ? { ...u, status: data.status } : u));
        setUserActionMsg('युझर स्टेटस बदलले!');
      }
    } catch (err) {
      alert('Error toggling user status: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('तुम्हाला खात्री आहे का? हा युझर कायमचा डिलीट केला जाईल.')) return;

    try {
      const res = await fetch(`${API_BASE}/developer/public-tenants/${userModalTenant.id}/users/${userId}`, {
        method: 'DELETE',
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      const data = await res.json();
      if (data.success) {
        setTenantUsers(prev => prev.filter(u => u.id !== userId));
        setUserActionMsg(data.message || 'युझर डिलीट केला.');
      } else {
        setUserActionError(data.message || 'युझर डिलीट करता आला नाही.');
      }
    } catch (err) {
      setUserActionError('एरर: ' + err.message);
    }
  };

  // ==========================================
  // VIEW 1: DEVELOPER LOGIN SCREEN
  // ==========================================
  if (!authToken) {
    return (
      <div style={{
        minHeight: '75vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        background: 'radial-gradient(circle at top, #1e293b 0%, #0f172a 100%)',
        borderRadius: 20,
        margin: '1.5rem auto',
        maxWidth: 520,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
      }}>
        <div style={{ width: '100%', color: '#ffffff' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem',
              boxShadow: '0 10px 20px -5px rgba(59, 130, 246, 0.5)'
            }}>
              <ShieldCheck size={32} color="#ffffff" />
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.6rem', fontWeight: 800 }}>
              {isMr ? 'डेव्हलपर सुरक्षित लॉगिन' : 'Developer Super-Admin Login'}
            </h2>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              {isMr 
                ? 'MilkPro Mitra SaaS लँडलॉर्ड पोर्टल (डेटाबेस: milkpromitra_main)' 
                : 'SaaS Landlord Administration (DB: milkpromitra_main)'}
            </div>
          </div>

          {loginError && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid #ef4444',
              color: '#fca5a5',
              padding: '0.8rem 1rem',
              borderRadius: 10,
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: '1.5rem'
            }}>
              <AlertTriangle size={18} color="#ef4444" />
              <span>{loginError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#cbd5e1', marginBottom: 6 }}>
                {isMr ? 'युझरनेम किंवा ईमेल' : 'Username or Email'}
              </label>
              <div style={{ position: 'relative' }}>
                <User size={18} color="#64748b" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  required
                  value={loginForm.username}
                  onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                  placeholder={isMr ? 'युझरनेम प्रविष्ट करा' : 'Enter username'}
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.6rem',
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: 10,
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#cbd5e1', marginBottom: 6 }}>
                {isMr ? 'पासवर्ड' : 'Password'}
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} color="#64748b" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.6rem',
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: 10,
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{
              background: 'rgba(51, 65, 85, 0.4)',
              border: '1px dashed #475569',
              borderRadius: 8,
              padding: '0.65rem 0.9rem',
              fontSize: '0.75rem',
              color: '#94a3b8'
            }}>
              🔑 <strong>{isMr ? 'चाचणी क्रेडेंशियल्स' : 'Demo Credentials'}:</strong><br />
              युझरनेम: <code style={{ color: '#38bdf8' }}>developer</code> | पासवर्ड: <code style={{ color: '#38bdf8' }}>password</code>
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              style={{
                background: loginLoading ? '#3b82f6' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '0.85rem',
                borderRadius: 10,
                fontSize: '0.95rem',
                fontWeight: 800,
                cursor: loginLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: '0 10px 20px -5px rgba(37, 99, 235, 0.5)'
              }}
            >
              {loginLoading ? (
                <>⏳ {isMr ? 'लॉगिन पडताळणी होत आहे...' : 'Verifying...'}</>
              ) : (
                <>
                  <ShieldCheck size={18} />
                  <span>{isMr ? 'सुरक्षित लॉगिन करा' : 'Sign In as Developer'}</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW 2: AUTHENTICATED DEVELOPER PORTAL
  // ==========================================
  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      
      {/* Top Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: 16,
        padding: '1.5rem',
        color: '#ffffff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ background: '#f59e0b', color: '#000', fontSize: '0.75rem', fontWeight: 800, padding: '3px 10px', borderRadius: 999 }}>
              DEVELOPER SUPER-ADMIN
            </span>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              लँडलॉर्ड डेटाबेस: <strong style={{ color: '#38bdf8' }}>milkpromitra_main</strong>
            </span>
            <span style={{ fontSize: '0.8rem', color: '#a7f3d0', background: 'rgba(16, 185, 129, 0.2)', padding: '2px 8px', borderRadius: 6 }}>
              ● {currentUser?.name || currentUser?.username || 'Lead Developer'}
            </span>
          </div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0 }}>
            {isMr ? 'SaaS Tenant व्यवस्थापन व युझर्स नियंत्रण' : 'SaaS Tenant & User Management'}
          </h2>
          <div style={{ fontSize: '0.85rem', color: '#cbd5e1', marginTop: 4 }}>
            {isMr 
              ? 'डेव्हलपर पोर्टलवरून नवीन Tenant जोडणे, त्याचे फीचर्स ठरवणे आणि त्याचे युझर्स (ॲडमिन/कॅशियर/डिलिव्हरी) नियंत्रित करणे.' 
              : 'Add tenants, manage feature flags, and create/manage users for each tenant.'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={loadData}
            style={{
              background: '#334155',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '0.6rem 0.9rem',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <RefreshCw size={14} />
            <span>{isMr ? 'रिफ्रेश' : 'Refresh'}</span>
          </button>

          <button
            onClick={() => setShowModal(true)}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              padding: '0.65rem 1.25rem',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
            }}
          >
            <PlusCircle size={16} />
            <span>{isMr ? 'नवीन Tenant जोडा' : 'Onboard Tenant'}</span>
          </button>

          <button
            onClick={handleLogout}
            style={{
              background: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              padding: '0.65rem 1rem',
              fontWeight: 800,
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
            }}
          >
            <LogOut size={15} />
            <span>{isMr ? 'लॉगआउट' : 'Logout'}</span>
          </button>
        </div>
      </div>

      {successMsg && (
        <div style={{
          background: '#ecfdf5',
          border: '1px solid #10b981',
          color: '#065f46',
          padding: '1rem',
          borderRadius: 10,
          fontWeight: 700,
          fontSize: '0.95rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <CheckCircle2 size={20} color="#10b981" />
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #ef4444', color: '#991b1b', padding: '1rem', borderRadius: 10, fontWeight: 600, fontSize: '0.9rem' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Tenants Grid */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1e293b' }}>
            {isMr ? `नोंदणीकृत Tenants (${tenants.length})` : `Registered Tenants (${tenants.length})`}
          </h3>
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
            {isMr ? 'प्रत्येक डेअरीचा स्वतंत्र डेटाबेस (tnt_{id})' : 'Dedicated isolated databases'}
          </span>
        </div>

        {loading ? (
          <div style={{ background: '#ffffff', padding: '3rem', textAlign: 'center', borderRadius: 12, border: '1px solid #e2e8f0', color: '#64748b' }}>
            {isMr ? 'डेटाबेसवरून Tenants लोड होत आहेत...' : 'Loading tenants...'}
          </div>
        ) : tenants.length === 0 ? (
          <div style={{ background: '#ffffff', padding: '3rem', textAlign: 'center', borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🏢</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>
              {isMr ? 'अद्याप कोणताही Tenant जोडलेला नाही' : 'No tenants found'}
            </div>
            <button
              onClick={() => setShowModal(true)}
              style={{ marginTop: 12, background: '#2563eb', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
            >
              {isMr ? 'पहिला Tenant ऑनबोर्ड करा' : 'Onboard First Tenant'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '1.25rem' }}>
            {tenants.map(t => {
              const plan = t.plan || plans.find(p => p.id === t.plan_id);
              const feat = t.feature || {};
              const isActive = t.status === 'ACTIVE';

              return (
                <div key={t.id} style={{
                  background: '#ffffff',
                  borderRadius: 14,
                  padding: '1.25rem',
                  border: isActive ? '1px solid #e2e8f0' : '1px solid #fecaca',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* Card Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          background: plan?.code === 'ADVANCED' ? '#ede9fe' : '#e0f2fe',
                          color: plan?.code === 'ADVANCED' ? '#6d28d9' : '#0369a1',
                          padding: '2px 8px',
                          borderRadius: 6
                        }}>
                          {plan?.name_mr || plan?.name_en || (t.plan_id === 2 ? 'ॲडव्हान्स प्लॅन' : 'बेसिक प्लॅन')}
                        </span>
                        <span style={{
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          background: '#f1f5f9',
                          color: '#334155',
                          padding: '2px 8px',
                          borderRadius: 6,
                          fontFamily: 'monospace'
                        }}>
                          DB: {t.database_name}
                        </span>
                      </div>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', margin: '0.5rem 0 2px 0' }}>
                        {t.brand_name_mr || t.name}
                      </h3>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {isMr ? 'मालक' : 'Owner'}: <strong>{t.owner_name}</strong> | 📞 {t.phone}
                      </div>
                    </div>

                    <button
                      onClick={() => handleToggleStatus(t.id)}
                      style={{
                        background: isActive ? '#ecfdf5' : '#fef2f2',
                        color: isActive ? '#059669' : '#dc2626',
                        border: `1px solid ${isActive ? '#a7f3d0' : '#fecaca'}`,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        cursor: 'pointer'
                      }}
                    >
                      {isActive ? '● ACTIVE' : '○ SUSPENDED'}
                    </button>
                  </div>

                  {/* TENANT DIRECT URL & WHATSAPP SHARING */}
                  <div style={{
                    background: '#eff6ff',
                    borderRadius: 10,
                    padding: '10px 12px',
                    border: '1px solid #bfdbfe',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#1e40af' }}>
                        🔗 {isMr ? 'थेट ॲप लॉगिन लिंक' : 'Direct App URL'}:
                      </span>
                      <span style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#0369a1', fontWeight: 700 }}>
                        slug: {t.slug}
                      </span>
                    </div>

                    <div style={{
                      background: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: 6,
                      padding: '5px 8px',
                      fontSize: '0.70rem',
                      fontFamily: 'monospace',
                      color: '#334155',
                      wordBreak: 'break-all',
                      userSelect: 'all'
                    }}>
                      {`${window.location.origin}/#${t.slug}`}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {/* Copy Link Button */}
                      <button
                        type="button"
                        onClick={() => handleCopyTenantLink(t.slug)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid #cbd5e1',
                          background: '#ffffff',
                          color: '#0284c7',
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4
                        }}
                      >
                        <span>📋 {copiedSlug === t.slug ? (isMr ? 'कॉपी झाली!' : 'Copied!') : (isMr ? 'लिंक कॉपी' : 'Copy Link')}</span>
                      </button>

                      {/* WhatsApp Share Button */}
                      <button
                        type="button"
                        onClick={() => handleShareWhatsApp(t)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: 'none',
                          background: '#16a34a',
                          color: '#ffffff',
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          boxShadow: '0 2px 4px rgba(22,163,74,0.25)'
                        }}
                      >
                        <span>💬 {isMr ? 'WhatsApp शेअर' : 'WhatsApp'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 8, fontSize: '0.8rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div>📍 {isMr ? 'शहर' : 'City'}: <strong>{t.city}</strong></div>
                    <div>📅 {isMr ? 'मुदत' : 'Valid Until'}: <strong>{t.valid_until ? new Date(t.valid_until).toLocaleDateString('mr-IN') : 'आजीवन'}</strong></div>
                    <div>🏢 {isMr ? 'केंद्रे मर्यादा' : 'Max Centers'}: <strong>कमाल {feat.max_centers_allowed || plan?.max_centers || 1}</strong></div>
                    <div>👥 {isMr ? 'युझर्स कोटा' : 'Max Users'}: <strong>कमाल {feat.max_users_allowed || plan?.max_users || 2}</strong></div>
                    <div>💳 {isMr ? 'पेमेंट स्थिती' : 'Payment Status'}: <strong style={{ color: t.payment_status === 'PAID' ? '#059669' : '#d97706' }}>{t.payment_status}</strong></div>
                    <div>📝 {isMr ? 'शेरा' : 'Remarks'}: <strong>{t.payment_remarks || '—'}</strong></div>
                  </div>

                  {/* PROMINENT USER MANAGEMENT BUTTON */}
                    <button
                      type="button"
                      onClick={() => handleReprovisionTenant(t)}
                      disabled={provisioningTenantId === t.id}
                      style={{
                        gridColumn: '1 / -1',
                        background: '#f0fdf4',
                        color: '#15803d',
                        border: '1.5px solid #86efac',
                        borderRadius: 8,
                        padding: '0.55rem 0.75rem',
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                      title={isMr ? 'डेटाबेस मायग्रेट करून सर्व टेबल्स तयार करा' : 'Migrate database tables'}
                    >
                      <RefreshCw size={14} className={provisioningTenantId === t.id ? 'spin' : ''} />
                      <span>{provisioningTenantId === t.id ? (isMr ? 'डेटाबेस मायग्रेट होत आहे...' : 'Migrating DB...') : (isMr ? '🔄 DB मायग्रेट / सिंक करा (Migrate DB)' : '🔄 Migrate & Sync DB')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => openResetDatabaseModal(t)}
                      style={{
                        gridColumn: '1 / -1',
                        background: '#fef2f2',
                        color: '#b91c1c',
                        border: '1.5px solid #fecaca',
                        borderRadius: 8,
                        padding: '0.52rem 0.75rem',
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                      title={isMr ? 'डेटाबेस टेबल्स निवडून ब्लॅंक / रेसेट करा' : 'Selectively blank/reset tenant database tables'}
                    >
                      <Trash2 size={14} />
                      <span>{isMr ? '🗑️ डेटाबेस रेसेट / Wipe Data' : '🗑️ Reset / Wipe Database'}</span>
                    </button>
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <button
                      onClick={() => openEditTenant(t)}
                      style={{
                        background: '#f8fafc',
                        color: '#0284c7',
                        border: '1.5px solid #bae6fd',
                        borderRadius: 8,
                        padding: '0.6rem 0.75rem',
                        fontSize: '0.82rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                    >
                      <span>✏️ {isMr ? 'माहिती व मुदत बदला' : 'Edit Subscription'}</span>
                    </button>

                    <button
                      onClick={() => openUserManagement(t)}
                      style={{
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '0.6rem 0.75rem',
                        fontSize: '0.82rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)'
                      }}
                    >
                      <Users size={15} />
                      <span>{isMr ? 'युझर्स व्यवस्थापन' : 'Manage Users'}</span>
                    </button>
                  </div>

                  {/* Feature Toggles (5 Business Features) */}
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>
                      {isMr ? 'प्लॅन फीचर्स व ॲक्सेस (5 FEATURE FLAGS):' : 'Feature Flags (5 Features):'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
                      {/* 1. Counter POS */}
                      <button
                        onClick={() => handleToggleFeature(t.id, 'counter_pos', feat.counter_pos)}
                        title={isMr ? "काऊंटर POS बिलिंग ऑन/ऑफ करा" : "Toggle Counter POS"}
                        style={{
                          background: feat.counter_pos ? '#dcfce7' : '#fef2f2',
                          color: feat.counter_pos ? '#15803d' : '#dc2626',
                          border: `1px solid ${feat.counter_pos ? '#86efac' : '#fca5a5'}`,
                          borderRadius: 6,
                          padding: '4px 9px',
                          fontSize: '0.73rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {feat.counter_pos ? '✓' : '✗'} 🛒 {isMr ? 'काऊंटर विक्री' : 'Counter POS'}
                      </button>

                      {/* 2. Daily Run-Sheet */}
                      <button
                        onClick={() => handleToggleFeature(t.id, 'daily_runsheet', feat.daily_runsheet)}
                        title={isMr ? "दैनिक वितरण पत्रक ऑन/ऑफ करा" : "Toggle Daily Run-Sheet"}
                        style={{
                          background: feat.daily_runsheet ? '#dcfce7' : '#fef2f2',
                          color: feat.daily_runsheet ? '#15803d' : '#dc2626',
                          border: `1px solid ${feat.daily_runsheet ? '#86efac' : '#fca5a5'}`,
                          borderRadius: 6,
                          padding: '4px 9px',
                          fontSize: '0.73rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {feat.daily_runsheet ? '✓' : '✗'} 🚚 {isMr ? 'दैनिक वितरण' : 'Daily Run-Sheet'}
                      </button>

                      {/* 3. Online Orders */}
                      <button
                        onClick={() => handleToggleFeature(t.id, 'online_orders', feat.online_orders)}
                        title={isMr ? "ग्राहक ऑनलाइन ऑर्डर्स ऑन/ऑफ करा" : "Toggle Online Orders"}
                        style={{
                          background: feat.online_orders ? '#dcfce7' : '#fef2f2',
                          color: feat.online_orders ? '#15803d' : '#dc2626',
                          border: `1px solid ${feat.online_orders ? '#86efac' : '#fca5a5'}`,
                          borderRadius: 6,
                          padding: '4px 9px',
                          fontSize: '0.73rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {feat.online_orders ? '✓' : '✗'} 📱 {isMr ? 'ग्राहक ऑर्डर्स' : 'Online Orders'}
                      </button>

                      {/* 4. Dairy Byproducts */}
                      <button
                        onClick={() => handleToggleFeature(t.id, 'dairy_byproducts', feat.dairy_byproducts)}
                        title={isMr ? "दुग्धजन्य उप-उत्पादने (पनीर, तूप इ.) ऑन/ऑफ करा" : "Toggle Dairy Byproducts"}
                        style={{
                          background: feat.dairy_byproducts ? '#dcfce7' : '#fef2f2',
                          color: feat.dairy_byproducts ? '#15803d' : '#dc2626',
                          border: `1px solid ${feat.dairy_byproducts ? '#86efac' : '#fca5a5'}`,
                          borderRadius: 6,
                          padding: '4px 9px',
                          fontSize: '0.73rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {feat.dairy_byproducts ? '✓' : '✗'} 🧀 {isMr ? 'दुग्धजन्य (Dairy)' : 'Dairy Byproducts'}
                      </button>

                      {/* 5. Bakery & Other Products */}
                      <button
                        onClick={() => handleToggleFeature(t.id, 'bakery_products', feat.bakery_products)}
                        title={isMr ? "बेकरी व इतर उत्पादने ऑन/ऑफ करा" : "Toggle Bakery Products"}
                        style={{
                          background: feat.bakery_products ? '#dcfce7' : '#fef2f2',
                          color: feat.bakery_products ? '#15803d' : '#dc2626',
                          border: `1px solid ${feat.bakery_products ? '#86efac' : '#fca5a5'}`,
                          borderRadius: 6,
                          padding: '4px 9px',
                          fontSize: '0.73rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {feat.bakery_products ? '✓' : '✗'} 🍞 {isMr ? 'बेकरी व इतर' : 'Bakery & Other'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* =========================================================================
          MODAL 1: TENANT USER MANAGEMENT (ADD / MODIFY / DELETE USERS)
          ========================================================================= */}
      {userModalTenant && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 18,
            width: '100%',
            maxWidth: 820,
            maxHeight: '92vh',
            overflowY: 'auto',
            padding: '1.75rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ background: '#e0f2fe', color: '#0284c7', fontSize: '0.75rem', fontWeight: 800, padding: '2px 8px', borderRadius: 6 }}>
                    DB: {userModalTenant.database_name}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {isMr ? 'डेव्हलपर युझर कंट्रोल' : 'Developer User Control'}
                  </span>
                </div>
                <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 900, color: '#0f172a' }}>
                  👥 {userModalTenant.brand_name_mr || userModalTenant.name} — युझर्स व्यवस्थापन
                </h3>
                <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 4 }}>
                  {isMr 
                    ? `सध्याचे युझर्स: ${tenantUsers.length} / कमाल ${maxUsersAllowed} (प्लॅन कोटा मर्यादा)` 
                    : `Users: ${tenantUsers.length} / Max ${maxUsersAllowed}`}
                </div>
              </div>

              <button
                onClick={() => setUserModalTenant(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b', padding: '0 6px' }}
              >
                ✕
              </button>
            </div>

            {/* User Action Alerts */}
            {userActionMsg && (
              <div style={{ background: '#ecfdf5', border: '1px solid #10b981', color: '#065f46', padding: '0.75rem 1rem', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', marginBottom: '1rem' }}>
                ✅ {userActionMsg}
              </div>
            )}
            {userActionError && (
              <div style={{ background: '#fef2f2', border: '1px solid #ef4444', color: '#dc2626', padding: '0.75rem 1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', marginBottom: '1rem' }}>
                ⚠️ {userActionError}
              </div>
            )}

            {/* ADD / EDIT USER FORM */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
                  {isEditingUser ? '✏️ युझर माहिती बदला (Edit User)' : '➕ नवीन युझर जोडा (Add New User)'}
                </h4>
                {isEditingUser && (
                  <button
                    onClick={() => {
                      setIsEditingUser(false);
                      setUserForm({ id: null, name: '', username: '', phone: '', password: '', role: 'DELIVERY', center_access: 'ALL', assigned_line_id: '' });
                    }}
                    style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    रद्द करा
                  </button>
                )}
              </div>

              <form onSubmit={handleSaveUser}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      पूर्ण नाव *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="उदा. राहुल शिंदे"
                      value={userForm.name}
                      onChange={e => setUserForm({ ...userForm, name: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      युझरनेम *
                    </label>
                    <input
                      type="text"
                      required
                      disabled={isEditingUser}
                      placeholder="उदा. rahul_delivery"
                      value={userForm.username}
                      onChange={e => setUserForm({ ...userForm, username: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', background: isEditingUser ? '#f1f5f9' : '#fff', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      मोबाईल नंबर
                    </label>
                    <input
                      type="text"
                      placeholder="98XXXXXXXX"
                      value={userForm.phone}
                      onChange={e => setUserForm({ ...userForm, phone: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      {isEditingUser ? 'नवीन पासवर्ड (पर्यायी)' : 'पासवर्ड *'}
                    </label>
                    <input
                      type="password"
                      required={!isEditingUser}
                      placeholder={isEditingUser ? 'बदलायचा असेल तरच टाका' : '••••••••'}
                      value={userForm.password}
                      onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      भूमिका (Role) *
                    </label>
                    <select
                      value={userForm.role}
                      onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="ADMIN">ADMIN (डेअरी मालक / व्यवस्थापक)</option>
                      <option value="CASHIER">CASHIER (काऊंटर POS ऑपरेटर)</option>
                      <option value="DELIVERY">DELIVERY (डिलिव्हरी बॉय)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      सेंटर ॲक्सेस
                    </label>
                    <select
                      value={userForm.center_access}
                      onChange={e => setUserForm({ ...userForm, center_access: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="ALL">सर्व केंद्रे (All Centers)</option>
                      {tenantCenters.map(c => (
                        <option key={c.id} value={c.id.toString()}>{c.name_mr || c.name_en}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 3 }}>
                      असाइन केलेला मार्ग (डिलिव्हरीसाठी)
                    </label>
                    <select
                      value={userForm.assigned_line_id}
                      onChange={e => setUserForm({ ...userForm, assigned_line_id: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="">कोणताही मार्ग नाही (None)</option>
                      {tenantLines.map(l => (
                        <option key={l.id} value={l.id.toString()}>{l.name_mr || l.name_en}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button
                    type="submit"
                    disabled={savingUser}
                    style={{
                      background: savingUser ? '#94a3b8' : '#0284c7',
                      color: '#fff',
                      border: 'none',
                      padding: '0.55rem 1.25rem',
                      borderRadius: 6,
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      cursor: savingUser ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingUser ? '⏳ सेव्ह होत आहे...' : (isEditingUser ? '💾 बदल सेव्ह करा' : '➕ युझर तयार करा')}
                  </button>
                </div>
              </form>
            </div>

            {/* USERS TABLE */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: '#334155' }}>
                  {isMr ? `या डेअरीतील सक्रिय युझर्स (${tenantUsers.length})` : `Existing Users (${tenantUsers.length})`}
                </h4>
              </div>

              {loadingUsers ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>युझर्स लोड होत आहेत...</div>
              ) : tenantUsers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b', background: '#f8fafc', borderRadius: 8 }}>
                  कोणतेही युझर्स सापडले नाहीत.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', color: '#334155', fontWeight: 800 }}>
                        <th style={{ padding: '0.65rem 0.75rem' }}>नाव / युझरनेम</th>
                        <th style={{ padding: '0.65rem 0.75rem' }}>मोबाईल</th>
                        <th style={{ padding: '0.65rem 0.75rem' }}>भूमिका (Role)</th>
                        <th style={{ padding: '0.65rem 0.75rem' }}>मार्ग / केंद्र</th>
                        <th style={{ padding: '0.65rem 0.75rem' }}>स्थिती</th>
                        <th style={{ padding: '0.65rem 0.75rem', textAlign: 'center' }}>कृती (Actions)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantUsers.map(u => {
                        const line = tenantLines.find(l => l.id === u.assigned_line_id);
                        const isBlocked = u.status === 'BLOCKED';

                        return (
                          <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', background: isBlocked ? '#fff1f2' : '#ffffff' }}>
                            <td style={{ padding: '0.65rem 0.75rem' }}>
                              <div style={{ fontWeight: 800, color: '#0f172a' }}>{u.name}</div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b', fontFamily: 'monospace' }}>@{u.username}</div>
                            </td>
                            <td style={{ padding: '0.65rem 0.75rem', color: '#475569' }}>
                              {u.phone || '—'}
                            </td>
                            <td style={{ padding: '0.65rem 0.75rem' }}>
                              <span style={{
                                fontSize: '0.7rem',
                                fontWeight: 800,
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: u.role === 'ADMIN' ? '#ede9fe' : (u.role === 'CASHIER' ? '#fef3c7' : '#e0f2fe'),
                                color: u.role === 'ADMIN' ? '#6d28d9' : (u.role === 'CASHIER' ? '#b45309' : '#0369a1')
                              }}>
                                {u.role === 'ADMIN' ? 'डेअरी मालक' : (u.role === 'CASHIER' ? 'काऊंटर POS' : 'डिलिव्हरी बॉय')}
                              </span>
                            </td>
                            <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.75rem', color: '#475569' }}>
                              {line ? line.name_mr : '—'}
                            </td>
                            <td style={{ padding: '0.65rem 0.75rem' }}>
                              <span style={{
                                fontSize: '0.7rem',
                                fontWeight: 800,
                                color: isBlocked ? '#dc2626' : '#16a34a'
                              }}>
                                {isBlocked ? '● BLOCKED' : '● ACTIVE'}
                              </span>
                            </td>
                            <td style={{ padding: '0.65rem 0.75rem', textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                                <button
                                  onClick={() => handleEditClick(u)}
                                  title="बदला (Edit)"
                                  style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#0284c7' }}
                                >
                                  <Edit2 size={13} />
                                </button>
                                <button
                                  onClick={() => handleToggleUserStatus(u.id)}
                                  title={isBlocked ? 'अनलॉक करा' : 'ब्लॉक करा'}
                                  style={{ background: isBlocked ? '#dcfce7' : '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: isBlocked ? '#15803d' : '#dc2626' }}
                                >
                                  {isBlocked ? <CheckCircle size={13} /> : <Ban size={13} />}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  title="हटवा (Delete)"
                                  style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#dc2626' }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setUserModalTenant(null)}
                style={{ background: '#334155', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                बंद करा (Close)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 2: ONBOARD NEW TENANT
          ========================================================================= */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            width: '100%',
            maxWidth: 620,
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '1.5rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {isMr ? 'नवीन Tenant ऑनबोर्ड करा' : 'Onboard New Tenant'}
                </h3>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  MySQL मध्ये स्वयंचलित डेटाबेस (tnt_{'{id}'}) व १० टेबल्स तयार होतील
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateTenant} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'डेअरीचे नाव *' : 'Dairy Name *'}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={isMr ? 'उदा. कृष्णाई डेअरी' : 'e.g. Krishnai Dairy'}
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मालकाचे नाव *' : 'Owner Name *'}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={isMr ? 'उदा. सचिन मोहिते' : 'e.g. Sachin Mohite'}
                    value={formData.owner_name}
                    onChange={e => setFormData({ ...formData, owner_name: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मोबाईल नंबर *' : 'Mobile Number *'}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="98XXXXXXXX"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'शहर / गाव *' : 'City / Town *'}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={isMr ? 'उदा. सातारा' : 'e.g. Satara'}
                    value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'पत्ता' : 'Address'}
                </label>
                <input
                  type="text"
                  placeholder={isMr ? 'दुकान क्र., चौक / रस्ता' : 'Shop No., Landmark'}
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'सबस्क्रिप्शन प्लॅन *' : 'Subscription Plan *'}
                  </label>
                  <select
                    value={formData.plan_id}
                    onChange={e => setFormData({ ...formData, plan_id: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                  >
                    {plans.length > 0 ? (
                      plans.map(p => (
                        <option key={p.id} value={p.id}>
                          {isMr ? p.name_mr : p.name_en} - ₹{p.price_monthly}/महिना
                        </option>
                      ))
                    ) : (
                      <>
                        <option value={1}>बेसिक प्लॅन (Single Center & User) - ₹500</option>
                        <option value={2}>ॲडव्हान्स प्लॅन (Multi-Center & Multi-User) - ₹1500</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मुदत (Valid Until) *' : 'Valid Until *'}
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_until}
                    onChange={e => setFormData({ ...formData, valid_until: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'पेमेंट स्थिती' : 'Payment Status'}
                  </label>
                  <select
                    value={formData.payment_status}
                    onChange={e => setFormData({ ...formData, payment_status: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                  >
                    <option value="PAID">{isMr ? 'पूर्ण भरणा (PAID)' : 'PAID'}</option>
                    <option value="DUE">{isMr ? 'बाकी (DUE)' : 'DUE'}</option>
                    <option value="PARTIAL">{isMr ? 'अंशतः भरणा (PARTIAL)' : 'PARTIAL'}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'पेमेंट शेरा / नियम' : 'Payment Remarks'}
                  </label>
                  <input
                    type="text"
                    placeholder={isMr ? 'उदा. वार्षिक ॲडव्हान्स भरले' : 'e.g. Yearly advance paid'}
                    value={formData.payment_remarks}
                    onChange={e => setFormData({ ...formData, payment_remarks: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.75rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.6rem 1.25rem', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#94a3b8' : '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.6rem 1.5rem',
                    borderRadius: 8,
                    fontWeight: 700,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
                  }}
                >
                  {submitting ? (isMr ? '⏳ डेटाबेस तयार होत आहे...' : '⏳ Provisioning...') : (isMr ? '🚀 Tenant व डेटाबेस तयार करा' : '🚀 Create Tenant & DB')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* EDIT TENANT & SUBSCRIPTION VALIDITY MODAL (Developer Super-Admin) */}
      {/* ========================================================================= */}
      {editTenantModal && editTenantData && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 150,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            maxWidth: 680,
            width: '100%',
            maxHeight: '92vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: '#0284c7',
              color: '#fff',
              padding: '1rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>
                  {isMr ? 'डेअरी तपशील, प्लॅन व सब्स्क्रिप्शन मुदत संपादन' : 'Edit Tenant Profile & Subscription'}
                </span>
                <div style={{ fontSize: '0.75rem', color: '#e0f2fe' }}>
                  {editTenantModal.name} (DB: {editTenantModal.database_name})
                </div>
              </div>
              <button
                onClick={() => setEditTenantModal(null)}
                style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveTenantDetails} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              {/* Basic Info: Name & Brand Name */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'डेअरीचे कायदेशीर नाव *' : 'Dairy Legal Name *'}
                  </label>
                  <input
                    required
                    type="text"
                    value={editTenantData.name}
                    onChange={e => setEditTenantData({ ...editTenantData, name: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'ब्रँड नाव (मराठीत)' : 'Brand Name (Marathi)'}
                  </label>
                  <input
                    type="text"
                    value={editTenantData.brand_name_mr}
                    onChange={e => setEditTenantData({ ...editTenantData, brand_name_mr: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Owner, Phone & City */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मालकाचे नाव *' : 'Owner Name *'}
                  </label>
                  <input
                    required
                    type="text"
                    value={editTenantData.owner_name}
                    onChange={e => setEditTenantData({ ...editTenantData, owner_name: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मोबाईल नंबर *' : 'Phone *'}
                  </label>
                  <input
                    required
                    type="text"
                    value={editTenantData.phone}
                    onChange={e => setEditTenantData({ ...editTenantData, phone: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'शहर / गाव *' : 'City *'}
                  </label>
                  <input
                    required
                    type="text"
                    value={editTenantData.city}
                    onChange={e => setEditTenantData({ ...editTenantData, city: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'पत्ता *' : 'Address *'}
                </label>
                <input
                  required
                  type="text"
                  value={editTenantData.address}
                  onChange={e => setEditTenantData({ ...editTenantData, address: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* Subscription & Plan Section (Highlighted Box) */}
              <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: 12, border: '1.5px solid #bae6fd' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0369a1', marginBottom: 8, textTransform: 'uppercase' }}>
                  📅 {isMr ? 'सब्स्क्रिप्शन प्लॅन, मुदत व कोटे (Subscription Validity & Plan)' : 'Subscription & Quotas'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      {isMr ? 'सॉफ्टवेअर प्लॅन निवडा' : 'Select Plan'}
                    </label>
                    <select
                      value={editTenantData.plan_id}
                      onChange={e => setEditTenantData({ ...editTenantData, plan_id: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="1">{isMr ? 'बेसिक प्लॅन (सिंगल सेंटर - कमाल २ युझर्स)' : 'Basic Plan (Single Center, 2 Users)'}</option>
                      <option value="2">{isMr ? 'ॲडव्हान्स प्लॅन (मल्टी सेंटर - कमाल ८ युझर्स)' : 'Advanced Plan (Multi Center, 8 Users)'}</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      {isMr ? 'सब्स्क्रिप्शन मुदत तारीख (Valid Until) *' : 'Subscription Expiry Date *'}
                    </label>
                    <input
                      type="date"
                      required
                      value={editTenantData.valid_until}
                      onChange={e => setEditTenantData({ ...editTenantData, valid_until: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: 800, boxSizing: 'border-box', background: '#fff' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      {isMr ? 'कमाल युझर्स कोटा' : 'Max Users Quota'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={editTenantData.max_users_allowed}
                      onChange={e => setEditTenantData({ ...editTenantData, max_users_allowed: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      {isMr ? 'कमाल केंद्रे मर्यादा' : 'Max Centers Quota'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={editTenantData.max_centers_allowed}
                      onChange={e => setEditTenantData({ ...editTenantData, max_centers_allowed: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      {isMr ? 'पेमेंट स्थिती' : 'Payment Status'}
                    </label>
                    <select
                      value={editTenantData.payment_status}
                      onChange={e => setEditTenantData({ ...editTenantData, payment_status: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="PAID">PAID (पूर्ण भरणा)</option>
                      <option value="DUE">DUE (शिल्लक)</option>
                      <option value="PARTIAL">PARTIAL (अंशतः)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: '0.65rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'पेमेंट शेरा (Payment Remarks)' : 'Payment Remarks'}
                  </label>
                  <input
                    type="text"
                    placeholder="उदा. वार्षिक आगाऊ भरणा - ₹१५,००० चेक क्र. १२३४"
                    value={editTenantData.payment_remarks}
                    onChange={e => setEditTenantData({ ...editTenantData, payment_remarks: e.target.value })}
                    style={{ width: '100%', padding: '0.45rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', background: '#fff', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Feature Flags Section (The 5 Switches in Modal) */}
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', marginBottom: 8, textTransform: 'uppercase' }}>
                  ⚙️ {isMr ? 'व्यवसाय फीचर्स चालू/बंद करा (5 Feature Flags)' : 'Toggle Business Features (5 Flags)'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 12 }}>
                  {isMr ? 'हे पर्याय बंद केल्यास क्लायंटच्या ॲपमध्ये साइडबार व कॅटलॉगमधून ते पूर्णपणे लपवले (Hide) जातील.' : 'Disabling any feature will hide it from the client app interface.'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', background: '#fff', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={editTenantData.counter_pos}
                      onChange={e => setEditTenantData({ ...editTenantData, counter_pos: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                    />
                    <span>🛒 {isMr ? 'काऊंटर विक्री (POS)' : 'Counter POS'}</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', background: '#fff', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={editTenantData.daily_runsheet}
                      onChange={e => setEditTenantData({ ...editTenantData, daily_runsheet: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                    />
                    <span>🚚 {isMr ? 'दैनिक वितरण (Run-Sheet)' : 'Daily Run-Sheet'}</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', background: '#fff', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={editTenantData.online_orders}
                      onChange={e => setEditTenantData({ ...editTenantData, online_orders: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                    />
                    <span>📱 {isMr ? 'ग्राहक ऑर्डर्स (Online)' : 'Online Orders'}</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', background: '#fff', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={editTenantData.dairy_byproducts}
                      onChange={e => setEditTenantData({ ...editTenantData, dairy_byproducts: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                    />
                    <span>🧀 {isMr ? 'दुग्धजन्य (Dairy Byproducts)' : 'Dairy Byproducts'}</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', background: '#fff', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', gridColumn: 'span 2' }}>
                    <input
                      type="checkbox"
                      checked={editTenantData.bakery_products}
                      onChange={e => setEditTenantData({ ...editTenantData, bakery_products: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                    />
                    <span>🍞 {isMr ? 'बेकरी व इतर उत्पादने (Bakery & Other Products)' : 'Bakery & Other Products'}</span>
                  </label>
                </div>
              </div>

              {/* Form Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setEditTenantModal(null)}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.6rem 1.2rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>

                <button
                  type="submit"
                  disabled={editSubmitting}
                  style={{
                    background: editSubmitting ? '#94a3b8' : '#0284c7',
                    color: '#fff',
                    border: 'none',
                    padding: '0.6rem 1.5rem',
                    borderRadius: 8,
                    fontWeight: 800,
                    cursor: editSubmitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)'
                  }}
                >
                  {editSubmitting ? (isMr ? 'बदल जतन होत आहेत...' : 'Saving...') : (isMr ? 'सर्व बदल जतन करा' : 'Save Changes')}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}


      {/* ========================================== */}
      {/* TENANT DATABASE SELECTIVE RESET MODAL      */}
      {/* ========================================== */}
      {resetModalTenant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 14 }}>
          <div style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', border: '1px solid #e2e8f0' }}>
            
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)', color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 900, fontSize: '1.02rem' }}>
                    {isMr ? 'डेटाबेस रेसेट / Wipe Data' : 'Reset / Wipe Database Tables'}
                  </div>
                  <div style={{ fontSize: '0.78rem', opacity: 0.9 }}>
                    {resetModalTenant.name} ({resetModalTenant.database_name})
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setResetModalTenant(null)}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '16px 18px' }}>
              
              {/* Critical Alert Warning */}
              <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>🛑</span>
                <div style={{ fontSize: '0.78rem', color: '#991b1b', lineHeight: 1.45 }}>
                  <strong>{isMr ? 'महत्त्वाची सूचना (Critical Warning):' : 'Critical Warning:'}</strong>{' '}
                  {isMr 
                    ? 'तुम्ही निवडलेल्या टेबल्समधील सर्व नोंदी कायमस्वरूपी नष्ट (TRUNCATE) केल्या जातील. हा डेटा पुन्हा मिळवता येणार नाही! केवळ चाचणीसाठी किंवा नव्याने सुरुवात करण्यासाठी हा पर्याय वापरा.'
                    : 'All records in the selected tables will be permanently deleted (TRUNCATED). This operation cannot be undone! Use only for resetting test data.'}
                </div>
              </div>

              {/* Success Result Banner */}
              {resetResult && (
                <div style={{ background: '#ecfdf5', border: '1.5px solid #86efac', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                  <div style={{ fontWeight: 800, color: '#166534', fontSize: '0.84rem' }}>
                    ✅ {resetResult.message}
                  </div>
                  {resetResult.cleared_tables && resetResult.cleared_tables.length > 0 && (
                    <ul style={{ margin: '6px 0 0 16px', fontSize: '0.76rem', color: '#15803d', padding: 0 }}>
                      {resetResult.cleared_tables.map((t, idx) => (
                        <li key={idx}>{t}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Table Selection Controls */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>
                  <input
                    type="checkbox"
                    checked={resetTables.length > 0 && selectedResetTables.length === resetTables.length}
                    onChange={handleSelectAllResetTables}
                    style={{ width: 17, height: 17, cursor: 'pointer' }}
                  />
                  <span>{isMr ? 'सर्व टेबल्स निवडा (Select All)' : 'Select All Tables'}</span>
                </label>

                <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 700 }}>
                  {isMr ? `${selectedResetTables.length} टेबल्स निवडले` : `${selectedResetTables.length} selected`}
                </span>
              </div>

              {/* Loading State */}
              {loadingResetTables ? (
                <div style={{ textAlign: 'center', padding: '24px', color: '#64748b', fontSize: '0.85rem' }}>
                  {isMr ? 'टेबल्स व डेटा मोजत आहे...' : 'Scanning tables...'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', paddingRight: 4 }}>
                  {resetTables.map(t => {
                    const isChecked = selectedResetTables.includes(t.table);
                    const isTransaction = t.category === 'transactions';
                    return (
                      <label
                        key={t.table}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: isChecked ? '1.5px solid #dc2626' : '1px solid #e2e8f0',
                          background: isChecked ? '#fff1f2' : '#ffffff',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleResetTable(t.table)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <div>
                            <div style={{ fontSize: '0.82rem', fontWeight: 800, color: isChecked ? '#991b1b' : '#1e293b' }}>
                              {isMr ? t.label_mr : t.label_en}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#64748b', fontFamily: 'monospace' }}>
                              {t.table} • <span style={{ color: isTransaction ? '#0284c7' : '#d97706', fontWeight: 700 }}>{isTransaction ? (isMr ? 'व्यवहार नोंद' : 'Transaction') : (isMr ? 'मास्टर डेटा' : 'Master')}</span>
                            </div>
                          </div>
                        </div>

                        <span style={{
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          padding: '2px 8px',
                          borderRadius: 12,
                          background: t.count > 0 ? '#fee2e2' : '#f1f5f9',
                          color: t.count > 0 ? '#b91c1c' : '#94a3b8'
                        }}>
                          {t.count} {isMr ? 'नोंदी' : 'rows'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Mandatory Acknowledgment Checkbox */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={resetConfirmChecked}
                    onChange={e => setResetConfirmChecked(e.target.checked)}
                    style={{ width: 17, height: 17, marginTop: 2, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.76rem', color: '#b91c1c', fontWeight: 800 }}>
                    {isMr 
                      ? 'होय, मी खात्री केली आहे की निवडलेल्या टेबल्समधील डेटा नष्ट होणार आहे आणि मला हे टेबल्स ब्लॅंक करायचे आहेत.' 
                      : 'Yes, I understand that data in selected tables will be permanently deleted and I want to proceed.'}
                  </span>
                </label>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setResetModalTenant(null)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer'
                  }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>

                <button
                  type="button"
                  onClick={handleExecuteResetDatabase}
                  disabled={selectedResetTables.length === 0 || !resetConfirmChecked || submittingReset}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: (selectedResetTables.length === 0 || !resetConfirmChecked || submittingReset) ? '#fca5a5' : '#dc2626',
                    color: '#ffffff',
                    fontWeight: 900,
                    fontSize: '0.84rem',
                    cursor: (selectedResetTables.length === 0 || !resetConfirmChecked || submittingReset) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                  }}
                >
                  <Trash2 size={14} />
                  <span>
                    {submittingReset 
                      ? (isMr ? 'टेबल्स ब्लॅंक होत आहेत...' : 'Wiping tables...') 
                      : (isMr ? `⚠️ निवडलेले ${selectedResetTables.length} टेबल्स ब्लॅंक करा` : `Reset ${selectedResetTables.length} Tables`)}
                  </span>
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
